import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ClientDTO,
  getClientById,
  ClientStatusDTO,
  getClientStatusListByVisibility,
  getClientStatusById,
  updateStatus,
} from 'wisestep-api';
import { ControlledTextInput, WsButton, WsSelect, WsSelectObj } from 'wisestep-base';
import { object, string } from 'yup';

import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

const validationSchema = object().shape({
  name: string().required('Please provide Name'),
});

interface ClientDetailsFormProps {
  setClientId?: (clientId: string) => void;
  getClient?: (client: ClientDTO) => void;
  defaultValues?: ClientDTO;
  source?: 'JobPostingForm' | 'ClientAdditionForm';
  onClose?: (value: boolean) => void;
}

const ClientStatusPopUpForm = ({
  defaultValues,
  onClose,
  setClientId,
  getClient,
}: ClientDetailsFormProps) => {
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues,
  });

  const [ourClientStatusOptions, setOurClientStatusOptions] = useState<WsSelectObj[]>([]);
  const [clientDTO, setClientDTO] = useState<ClientStatusDTO[]>([]);
  const [defaultClientStatusName, setDefaultClientStatusName] = useState<string>(
    defaultValues?.clientStatus ? defaultValues?.clientStatus?.clientStatusName : ''
  );
  const setNotification = useNotificationStore((state) => state.setNotification);

  const { handleSubmit, setValue } = methods;

  const onSubmit = async (data) => {
    const selectedOption = ourClientStatusOptions.find(
      (option) => option.value === data.clientStatusId
    );
    if (selectedOption) {
      const selectedClientStatusDTO = clientDTO.find(
        (option) => option.id === selectedOption.value
      );
      if (selectedClientStatusDTO && selectedClientStatusDTO.isMarkInactive) {
        setNotification(warning('Selected status is inactive!'));
        return;
      }
    }
    try {
      const res = await getClientById(data.id);
      const client = res.data.data;
      const updateClientStatusRes = await getClientStatusById(data.clientStatusId ?? '');
      const clientStatusDTO = updateClientStatusRes.data;
      const updateClientStatus = {
        ...clientStatusDTO,
        id: data.clientStatusId,
      };
      const updatedClient = {
        ...client,
        clientStatus: updateClientStatus,
      };
      const response = await updateStatus(data.id, updatedClient);
      if (response.data) {
        setClientId && setClientId(response.data.id);
        getClient && getClient(response.data);
      }
      onClose && onClose(false);
      setNotification(success('Client status has been Updated successfully !!'));
    } catch (error) {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
  };

  useEffect(() => {
    const getClientStatusListDetails = async () => {
      const response = await getClientStatusListByVisibility();
      setClientDTO(response.data);
      const optionsArray: WsSelectObj[] = [];
      response.data.forEach((obj: ClientStatusDTO) => {
        if (obj.makeDefaultStatus) {
          setDefaultClientStatusName(obj.id);
          methods.setValue('clientStatusId', obj.id);
        }
        optionsArray.push({
          label: obj.clientStatusName as string,
          value: obj.id ?? '',
        });
      });

      defaultValues?.clientStatus?.clientStatusName
        ? setValue(
            'clientStatusId',
            optionsArray.filter(
              (item) => item.label == defaultValues.clientStatus?.clientStatusName
            )[0]?.value
          )
        : setValue('clientStatusId', defaultClientStatusName);
      setOurClientStatusOptions(optionsArray);
    };
    getClientStatusListDetails();
  }, [defaultClientStatusName, defaultValues?.clientStatus?.clientStatusName, methods, setValue]);

  const handleClientStatusChange = (selectedValue: string) => {
    const selectedOption = ourClientStatusOptions.find((option) => option.value === selectedValue);
    if (selectedOption) {
      const correspondingData = clientDTO.find((data) => data.id === selectedValue);
      if (correspondingData && correspondingData.isMarkInactive) {
        setNotification(warning('Selected status is inactive!'));
        return;
      } else {
        setValue('clientStatusId', selectedValue);
      }
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container spacing={1} direction="column">
              <Grid item xs={4}>
                <ControlledTextInput
                  name="name"
                  type="text"
                  fullWidth
                  labelText="Client Name"
                  variant="outlined"
                  showMandatorySymbol={true}
                />
              </Grid>
              <Grid item xs={4}>
                <WsSelect
                  label="Client Status Name"
                  name="clientStatusId"
                  selectArray={ourClientStatusOptions}
                  defaultValue={defaultClientStatusName}
                  size="small"
                  onChangeValue={(selectedValue) => handleClientStatusChange(selectedValue)}
                />
              </Grid>
            </Grid>
          </Box>
          <Stack
            p={1}
            mt={5}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="contained" size="medium" type="submit">
              Update
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};

export default ClientStatusPopUpForm;
